import { useState } from 'react';
import { Shield, UserPlus, Trash2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useModerators, useAddModerator, useRemoveModerator } from '@/hooks/useForum';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { sv } from 'date-fns/locale';

export function ForumAdminPanel() {
  const [email, setEmail] = useState('');
  const { data: moderators, isLoading, error } = useModerators();
  const addModerator = useAddModerator();
  const removeModerator = useRemoveModerator();

  const handleAddModerator = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    try {
      await addModerator.mutateAsync(email.trim());
      toast.success('Moderator tillagd');
      setEmail('');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte lägga till moderator');
    }
  };

  const handleRemoveModerator = async (userId: string, displayName: string) => {
    try {
      await removeModerator.mutateAsync(userId);
      toast.success(`${displayName} är inte längre moderator`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ta bort moderator');
    }
  };

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-destructive">Du har inte behörighet att se denna sida.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Admin-panel
        </CardTitle>
        <CardDescription>
          Hantera moderatorer för forumet
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <form onSubmit={handleAddModerator} className="flex gap-2">
          <Input
            type="email"
            placeholder="Ange e-postadress för ny moderator"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1"
          />
          <Button type="submit" disabled={addModerator.isPending || !email.trim()}>
            <UserPlus className="h-4 w-4 mr-2" />
            Lägg till
          </Button>
        </form>

        <div className="space-y-4">
          <h3 className="font-medium flex items-center gap-2">
            <Users className="h-4 w-4" />
            Nuvarande moderatorer och admins
          </h3>

          {isLoading ? (
            <p className="text-muted-foreground">Laddar...</p>
          ) : moderators && moderators.length > 0 ? (
            <div className="space-y-2">
              {moderators.map((mod) => (
                <div 
                  key={mod.id} 
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="font-medium">{mod.display_name}</p>
                      <p className="text-sm text-muted-foreground">{mod.email}</p>
                    </div>
                    <Badge variant={mod.role === 'admin' ? 'default' : 'secondary'}>
                      {mod.role === 'admin' ? 'Admin' : 'Moderator'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">
                      Sedan {formatDistanceToNow(new Date(mod.role_assigned_at), { locale: sv })}
                    </span>
                    {mod.role === 'moderator' && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Ta bort moderator?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {mod.display_name} kommer inte längre kunna moderera forumet.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Avbryt</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleRemoveModerator(mod.id, mod.display_name)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Ta bort
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">Inga moderatorer ännu.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
